// Globals
import { useCallback, useEffect, ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { getOrderDetails, getOrderProductsList, ordersSelector, updateOrderProducts } from '../../feature';

// Components
import {
  StyledContainerWrapper,
  StyledContainerTitle,
  StyledInputLabel,
  StyledListWrapper,
  StyledNothingFound,
  StyledAddProductsButton,
} from './AddVariationsContainerStyles';
import { IFormInstance, showErrorToast, showSuccessToast } from '@/components';
import { Input } from '@/ui';
import AddProductListItem from './AddProductListItem';
import { CompositionChangeReasonModal } from '../../components';
import { hasUnsavedOrderComments, OrderDetailsFormFieldsNames, requiresCompositionChangeReason } from '../../helpers';
import { useModal } from '@/hooks';
import { IApiError } from '@/models/apiError.model';
import { showApiErrors } from '@/utils';

interface AddProductContainerProps {
  closeAddProductDrawer: () => void;
  alreadyAddedProducts: { [key: string]: number } | undefined;
  productsForm?: IFormInstance;
}

const AddProductContainer = ({
  closeAddProductDrawer,
  alreadyAddedProducts,
  productsForm,
}: AddProductContainerProps) => {
  const { t } = useTranslation(['orders']);
  const dispatch = useAppDispatch();
  const { order, orderProductsList, isLoadingOrderProducts, isUpdatingProducts } = useAppState(ordersSelector);
  const [productsToAdd, setProductsToAdd] = useState<{ id: string; quantity: number }[]>([]);
  const { isOpened: isReasonModalOpen, openModal: openReasonModal, closeModal: closeReasonModal } = useModal();

  const debouncedFetchProducts = useCallback(
    debounce((searchText: string) => {
      dispatch(getOrderProductsList({ region_id: order?.delivery?.region_id as string, dto: { sku: searchText } }));
    }, 1000),
    [],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    debouncedFetchProducts(e.target.value);
  };

  const saveProducts = async (reason?: string) => {
    await dispatch(
      updateOrderProducts({
        order_id: order?.id as string,
        products: productsToAdd,
        add_new: true,
        reason,
      }),
    ).unwrap();
    closeAddProductDrawer();
    await dispatch(getOrderDetails(order?.id as string));
    showSuccessToast({ message: t('order_data_saved') });
  };

  const handleUpdateProductsError = (error: unknown) => {
    const apiError = error as IApiError;

    if (apiError?.errors && Object.keys(apiError.errors).length) {
      showApiErrors(apiError);

      return;
    }

    showErrorToast({ message: apiError?.message || t('orders_add_product_drawer_already_added') });
  };

  const handleUpdateProducts = async () => {
    try {
      const comments = productsForm?.getFieldValue(OrderDetailsFormFieldsNames.COMMENTS);

      if (hasUnsavedOrderComments(comments)) {
        showErrorToast({ message: t('order_unsent_comment_warning') });
        return;
      }

      const alreadyInOrder = productsToAdd.some((product) => !!alreadyAddedProducts?.[product.id]);

      if (alreadyInOrder) {
        showErrorToast({ message: t('orders_add_product_drawer_already_added') });
        return;
      }

      if (requiresCompositionChangeReason(order?.status, order?.payment_status)) {
        openReasonModal();
        return;
      }

      await saveProducts();
    } catch (error) {
      handleUpdateProductsError(error);
    }
  };

  const handleConfirmReason = async (reason: string) => {
    try {
      await saveProducts(reason);
      closeReasonModal();
    } catch (error) {
      handleUpdateProductsError(error);
    }
  };

  useEffect(() => {
    dispatch(getOrderProductsList({ region_id: order?.delivery?.region_id as string }));
  }, []);

  return (
    <StyledContainerWrapper>
      <StyledContainerTitle>{t('orders_add_product_drawer_title')}</StyledContainerTitle>
      <div>
        <StyledInputLabel htmlFor="product">{t('orders_add_product_drawer_input_label')}</StyledInputLabel>
        <Input id="product" placeholder={t('orders_add_product_drawer_search_placeholder')} onChange={handleSearch} />
      </div>
      <StyledListWrapper>
        {orderProductsList?.data?.map((product) => (
          <AddProductListItem
            key={product.id}
            product={product}
            isAlreadyAdded={!!alreadyAddedProducts?.[product.id]}
            setProductsToAdd={setProductsToAdd}
            isLoading={isLoadingOrderProducts}
          />
        ))}
        {!orderProductsList?.data.length && !isLoadingOrderProducts && (
          <StyledNothingFound>{t('orders_add_product_drawer_nothing_found')}</StyledNothingFound>
        )}
      </StyledListWrapper>
      <StyledAddProductsButton
        onClick={handleUpdateProducts}
        loading={isLoadingOrderProducts || isUpdatingProducts}
        disabled={!productsToAdd.length}
      >
        {t('orders_add_product_drawer_btn')}
      </StyledAddProductsButton>
      <CompositionChangeReasonModal
        isOpen={isReasonModalOpen}
        isLoading={isUpdatingProducts}
        onClose={closeReasonModal}
        onConfirm={handleConfirmReason}
      />
    </StyledContainerWrapper>
  );
};

export default AddProductContainer;
